import { firstRelation } from "@/lib/relations";
import { callRpc } from "@/lib/api/rpc";
import { supabase } from "@/lib/supabase";

export interface ProductWizardDraftRow {
  id: string;
  title: string;
  step: string;
  payload: Record<string, unknown>;
}

export function upsertProductWizardDraft(params: {
  orgId: string;
  draftId: string | null;
  title: string;
  step: string;
  payload: Record<string, unknown>;
}): Promise<string> {
  return callRpc<string>("upsert_product_wizard_draft", {
    p_org_id: params.orgId,
    p_draft_id: params.draftId,
    p_title: params.title,
    p_step: params.step,
    p_payload: params.payload,
  });
}

export async function discardProductWizardDraft(orgId: string, draftId: string): Promise<{ ok: boolean; removed_storage_keys?: string[] }> {
  const result = await callRpc<{ ok: boolean; removed_storage_keys?: string[] } | null>("discard_product_wizard_draft", {
    p_org_id: orgId,
    p_draft_id: draftId,
  });
  // A successful HTTP response alone does not confirm the domain operation.
  // Keep the editor intact if the acknowledgement cannot be interpreted.
  if (result?.ok !== true || (result.removed_storage_keys !== undefined
    && (!Array.isArray(result.removed_storage_keys)
      || result.removed_storage_keys.some((key) => typeof key !== "string")))) {
    throw new Error("Could not confirm that the draft was discarded.");
  }
  return result;
}

export function attachWizardDraftMedia(params: {
  orgId: string;
  draftId: string;
  mediaAssetId: string;
  position?: number;
  isPrimary?: boolean;
}): Promise<{ ok: boolean; id: string; position: number; is_primary: boolean }> {
  return callRpc("attach_wizard_draft_media", {
    p_org_id: params.orgId,
    p_draft_id: params.draftId,
    p_media_asset_id: params.mediaAssetId,
    p_position: params.position ?? null,
    p_is_primary: params.isPrimary ?? null,
    p_role: "gallery",
    p_alt_text: "",
  });
}

export function detachWizardDraftMedia(orgId: string, draftId: string, draftMediaId: string): Promise<{ ok: boolean; archived?: boolean; storage_key?: string | null }> {
  return callRpc("detach_wizard_draft_media", {
    p_org_id: orgId,
    p_draft_id: draftId,
    p_draft_media_id: draftMediaId,
  });
}

export function transferWizardDraftMedia(orgId: string, draftId: string, productId: string): Promise<{ ok: boolean; transferred: number }> {
  return callRpc("transfer_wizard_draft_media", {
    p_org_id: orgId,
    p_draft_id: draftId,
    p_product_id: productId,
  });
}

export interface WizardDraftMediaRow {
  id: string;
  media_asset_id: string;
  position: number;
  is_primary: boolean;
  role: string;
  alt_text: string;
  media_assets?: { id: string; storage_key: string; original_filename: string | null; mime_type: string | null } | null;
}

export async function loadWizardDraftMedia(orgId: string, draftId: string): Promise<WizardDraftMediaRow[]> {
  const { data, error } = await supabase
    .from("product_wizard_draft_media")
    .select("id, media_asset_id, position, is_primary, role, alt_text, media_assets(id, storage_key, original_filename, mime_type)")
    .eq("org_id", orgId)
    .eq("draft_id", draftId)
    .order("position", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<WizardDraftMediaRow & { media_assets?: WizardDraftMediaRow["media_assets"] | WizardDraftMediaRow["media_assets"][] }>).map((row) => ({
    ...row,
    media_assets: firstRelation(row.media_assets) ?? null,
  }));
}

export async function loadProductWizardDraft(orgId: string, draftId: string): Promise<ProductWizardDraftRow | null> {
  const { data, error } = await supabase
    .from("product_wizard_drafts")
    .select("id, step, payload, title")
    .eq("org_id", orgId)
    .eq("id", draftId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProductWizardDraftRow | null) ?? null;
}
